import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Trash, FloppyDisk, MagnifyingGlass, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

function summarize(c = {}) {
  const bits = [];
  if (c.keyword) bits.push(`"${c.keyword}"`);
  if (c.country) bits.push(c.country);
  if (c.arrangement) bits.push(c.arrangement);
  if (c.employment_type) bits.push(c.employment_type);
  if (c.language) bits.push(c.language);
  if (c.only_eligible) bits.push("eligible only");
  if (c.min_match) bits.push(`match ≥ ${c.min_match}%`);
  if (c.eligibility?.length) bits.push(c.eligibility.join("/"));
  return bits;
}

export default function SavedSearches() {
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(null);
  const load = () => api.get("/saved-searches").then((r) => setItems(r.data.searches));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/saved-searches/${id}`); toast("Search deleted"); load(); };

  const toggle = async (s) => {
    const next = !s.alerts_enabled;
    try {
      await api.put(`/saved-searches/${s.id}/alerts`, { alerts_enabled: next });
      setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, alerts_enabled: next } : x)));
      toast.success(next ? "Daily email alerts on" : "Alerts off");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendNow = async (s) => {
    setSending(s.id);
    try {
      const { data } = await api.post(`/saved-searches/${s.id}/send-digest`);
      data.sent ? toast.success(data.message) : toast(data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(null); }
  };

  return (
    <div>
      <PageHeader title="Saved searches" subtitle="Reusable criteria sets. Turn on daily email alerts to get only the jobs that pass your hard rules — delivered each morning." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="saved-searches-list">
        {items.length === 0 && (
          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-white p-10 text-center">
            <FloppyDisk size={32} className="mx-auto text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No saved searches yet. Save one from the Find jobs page.</p>
            <Link to="/jobs" className="mt-3 inline-block text-sm font-medium text-slate-900 underline">Go to search</Link>
          </div>
        )}
        {items.map((s) => (
          <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col" data-testid={`search-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-heading font-bold text-slate-900">{s.name}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {summarize(s.criteria).length === 0 && <span className="text-xs text-slate-400">All jobs</span>}
                  {summarize(s.criteria).map((b, i) => <Badge key={i} variant="secondary" className="font-normal">{b}</Badge>)}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(s.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-search-${s.id}`}><Trash /></Button>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <EnvelopeSimple className="text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Daily email alerts</span>
                <Switch checked={!!s.alerts_enabled} onCheckedChange={() => toggle(s)} data-testid={`alert-toggle-${s.id}`} />
              </label>
              {s.last_sent_at && <span className="text-xs text-slate-400">Last sent: {s.last_sent_count ?? 0} job(s)</span>}
            </div>

            <div className="mt-3 flex gap-2">
              <Link to="/jobs" className="flex-1"><Button variant="outline" size="sm" className="w-full border-slate-300 gap-1.5"><MagnifyingGlass /> Run in search</Button></Link>
              <Button variant="outline" size="sm" onClick={() => sendNow(s)} disabled={sending === s.id} data-testid={`send-digest-${s.id}`} className="border-slate-300 gap-1.5">
                <PaperPlaneTilt /> {sending === s.id ? "Sending…" : "Email me now"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
